import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import { useAuthStore } from '../../store/authStore';
import '../MfaVerifyPage/MfaVerifyPage.css';
import './MfaEnrollPage.css';

/**
 * 2차 인증 등록 — 관리자 권한을 받은 사람이 **처음 한 번** 지나는 화면. 2026-08-25 신규.
 *
 * ★★ 왜 만들었나 — **여기가 막다른 길이었다.**
 *   `ADMIN`·`SYSTEM_ADMIN` 은 2차 인증이 강제다(`auth-helpers.ts` `determineMfaState`).
 *   자격이 없으면 로그인 응답이 `mfa_enroll_required` 로 오는데, `MfaVerifyPage` 는 그걸 받으면
 *   `/login` 으로 되돌려 보내기만 했고(코드 주석: *"향후 enroll page (P1-20b 후속)"*),
 *   `LoginPage` 는 그 상태를 읽지 않았다. 결과는 **아무 말 없이 로그인 화면으로 튕기는 무한 고리** —
 *   새로 관리자가 된 사람은 그 순간부터 못 들어온다.
 *   2026-08-25 실제로 겪었다: 08:28 에 한 계정이 STAFF → SYSTEM_ADMIN 으로 올라간 뒤
 *   다음 로그인부터 막혔다(그전 세션은 refresh 로 살아 있어 더 헷갈렸다).
 *
 * 서버는 이미 다 있었다 — 화면만 없었다(신규 엔드포인트 0건):
 *   · `POST /auth/mfa/enroll` — 본문 `{ currentPassword }`, mfa-pending 토큰 허용,
 *     `{ otpauth_uri, secret_base32, backup_codes[], backup_codes_warning }` 반환. 분당 5회.
 *   · `POST /auth/mfa/verify` — `{ code, method }` + 같은 토큰 → 정식 세션(쿠키).
 *
 * ⚠ **비밀번호를 한 번 더 받는다** — 서버가 요구한다. mfa-pending 토큰만으로 등록되면
 *   비밀번호가 새어 나갔을 때 공격자가 먼저 등록해 **본인을 영구히 잠글 수 있다**(서버 주석 HIGH-4).
 *
 * ⚠ **QR 은 그리지 않는다** — 이 저장소에 QR 라이브러리가 없다(전수 확인). 대신 키를 네 자씩
 *   끊어 보여 주고 복사 단추를 둔다. 인증 앱은 전부 수동 입력을 지원한다.
 *   QR 이 필요하면 라이브러리를 들이는 별도 판단이 필요하다.
 */

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

interface EnrollState {
  tempToken?: string;
  tempTokenExpiresAt?: number;
  redirectTo?: string;
}

interface EnrollResponse {
  otpauth_uri: string;
  secret_base32: string;
  backup_codes: string[];
  backup_codes_warning?: string;
}

interface UserMe {
  user: {
    id: string; email: string; name: string; role: string;
    institutionId: string; avatarUrl?: string | null;
  };
}

interface ApiError { code?: string; message?: string }

/** 키를 네 자씩 끊는다 — 손으로 옮겨 적을 때 훨씬 덜 틀린다. */
function groupSecret(secret: string): string {
  return (secret.match(/.{1,4}/g) ?? [secret]).join(' ');
}

type Step = 'password' | 'register' | 'confirm';

export function MfaEnrollPage(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const state = (location.state ?? {}) as EnrollState;
  const tempToken = state.tempToken;
  const redirectTo = state.redirectTo ?? '/dashboard';

  const [step, setStep] = useState<Step>('password');
  const [password, setPassword] = useState('');
  const [enroll, setEnroll] = useState<EnrollResponse | null>(null);
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState<'secret' | 'codes' | null>(null);
  const [savedAck, setSavedAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(null), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  // 직접 주소로 들어오면 할 수 있는 게 없다 — 토큰은 로그인 응답에서만 온다.
  if (!tempToken) return <Navigate to="/login" replace />;

  async function copy(text: string, what: 'secret' | 'codes') {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
    } catch {
      setError('클립보드에 복사하지 못했습니다 — 화면의 값을 직접 옮겨 적어 주세요.');
    }
  }

  function friendly(err: unknown, fallback: string): string {
    const ax = err as AxiosError<ApiError>;
    const c = ax.response?.data?.code;
    if (c === 'MFA_ALREADY_ENROLLED') return '이미 2차 인증이 등록돼 있습니다. 로그인 화면에서 다시 시도하세요.';
    if (c === 'INVALID_PASSWORD') return '비밀번호가 맞지 않습니다.';
    if (c === 'MFA_INVALID') return '코드가 맞지 않습니다. 인증 앱의 현재 6자리를 다시 확인하세요.';
    if (ax.response?.status === 429) return '시도가 너무 잦습니다. 1분 뒤에 다시 시도하세요.';
    if (ax.response?.status === 401) return '로그인 대기 시간이 지났습니다. 로그인부터 다시 하세요.';
    return ax.response?.data?.message ?? fallback;
  }

  /** 1단계 — 비밀번호 재확인 후 키·백업 코드를 받는다. */
  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    if (busy || password.trim() === '') return;
    setBusy(true); setError(null);
    try {
      // bare axios — mfa-pending 토큰은 아직 정식 세션이 아니라 apiClient 인터셉터를 태우지 않는다.
      const { data } = await axios.post<EnrollResponse>(
        `${baseURL}/auth/mfa/enroll`,
        { currentPassword: password },
        { headers: { Authorization: `Bearer ${tempToken}` }, withCredentials: true, timeout: 30_000 },
      );
      setEnroll(data);
      setStep('register');
    } catch (err) {
      setError(friendly(err, '2차 인증을 시작하지 못했습니다.'));
    } finally {
      setBusy(false);
    }
  }

  /** 3단계 — 앱이 만든 6자리로 확인하면 정식 세션이 열린다. */
  async function submitCode(e: FormEvent) {
    e.preventDefault();
    if (busy || !/^\d{6}$/.test(code.trim())) return;
    setBusy(true); setError(null);
    try {
      await axios.post(
        `${baseURL}/auth/mfa/verify`,
        { code: code.trim(), method: 'totp' },
        { headers: { Authorization: `Bearer ${tempToken}` }, withCredentials: true, timeout: 30_000 },
      );
      const me = await axios.get<UserMe>(`${baseURL}/users/me`, { withCredentials: true, timeout: 30_000 });
      setSession({
        id: Number(me.data.user.id),
        email: me.data.user.email,
        name: me.data.user.name,
        role: me.data.user.role as never,
        institutionId: Number(me.data.user.institutionId),
        avatarUrl: me.data.user.avatarUrl ?? null,
      });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(friendly(err, '확인하지 못했습니다.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mfa-page">
      <div className="mfa-card mfa-card--enroll">
        <h1 className="mfa-card__heading">2차 인증 등록</h1>
        <p className="mfa-card__subheading">
          관리자 권한에는 2차 인증이 필요합니다. 한 번만 등록하면 다음부터는 6자리 코드만 넣으면 됩니다.
        </p>

        <ol className="mfe-steps" aria-label="진행 단계">
          <li className={step === 'password' ? 'is-now' : 'is-done'}>비밀번호 확인</li>
          <li className={step === 'register' ? 'is-now' : step === 'confirm' ? 'is-done' : ''}>앱에 등록</li>
          <li className={step === 'confirm' ? 'is-now' : ''}>코드 확인</li>
        </ol>

        {error && <p className="mfa-card__error" role="alert">{error}</p>}

        {step === 'password' && (
          <form onSubmit={submitPassword}>
            <div className="mfa-card__field">
              <label className="mfa-card__label" htmlFor="mfe-pw">비밀번호</label>
              <input
                id="mfe-pw" type="password" autoComplete="current-password" autoFocus
                value={password} onChange={(e) => setPassword(e.target.value)}
              />
              <p className="mfa-card__hint">
                지금 로그인하려던 계정의 비밀번호입니다. 남이 먼저 등록해 계정을 잠그는 일을 막기 위해 한 번 더 확인합니다.
              </p>
            </div>
            <button type="submit" className="mfa-card__button" disabled={busy || password.trim() === ''}>
              {busy ? '확인 중…' : '다음'}
            </button>
          </form>
        )}

        {step === 'register' && enroll && (
          <>
            <div className="mfe-block">
              <h2 className="mfe-block__title">① 인증 앱에 키를 넣습니다</h2>
              <p className="mfa-card__hint">
                Google OTP · Microsoft Authenticator 등에서 <b>“키 직접 입력”</b> 을 고르고 아래 값을 넣으세요.
              </p>
              <div className="mfe-secret">
                <code>{groupSecret(enroll.secret_base32)}</code>
                <button type="button" className="mfe-copy" onClick={() => void copy(enroll.secret_base32, 'secret')}>
                  {copied === 'secret' ? '복사됨' : '복사'}
                </button>
              </div>
              <p className="mfa-card__hint">계정 이름은 아무렇게나 적어도 됩니다. 시간 기반(TOTP) 6자리로 두세요.</p>
            </div>

            <div className="mfe-block">
              <h2 className="mfe-block__title">② 백업 코드를 안전한 곳에 옮겨 적습니다</h2>
              <p className="mfe-warn" role="alert">
                {enroll.backup_codes_warning ?? '백업 코드는 지금 한 번만 보입니다. 창을 닫으면 다시 볼 수 없습니다.'}
                {' '}휴대폰을 잃어버렸을 때 이 코드로만 들어올 수 있습니다.
              </p>
              <ul className="mfe-codes">
                {enroll.backup_codes.map((c) => <li key={c}><code>{c}</code></li>)}
              </ul>
              <button type="button" className="mfe-copy" onClick={() => void copy(enroll.backup_codes.join('\n'), 'codes')}>
                {copied === 'codes' ? '복사됨' : '전부 복사'}
              </button>
              <label className="mfe-ack">
                <input type="checkbox" checked={savedAck} onChange={(e) => setSavedAck(e.target.checked)} />
                백업 코드를 안전한 곳에 저장했습니다
              </label>
            </div>

            <button
              type="button" className="mfa-card__button"
              disabled={!savedAck} onClick={() => { setError(null); setStep('confirm'); }}
            >
              다음
            </button>
          </>
        )}

        {step === 'confirm' && (
          <form onSubmit={submitCode}>
            <div className="mfa-card__field">
              <label className="mfa-card__label" htmlFor="mfe-code">인증 앱의 6자리</label>
              <input
                id="mfe-code" inputMode="numeric" autoComplete="one-time-code" autoFocus
                maxLength={6} value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
              <p className="mfa-card__hint">코드는 30초마다 바뀝니다. 바뀌기 직전이면 다음 코드를 기다렸다 넣으세요.</p>
            </div>
            <button type="submit" className="mfa-card__button" disabled={busy || !/^\d{6}$/.test(code.trim())}>
              {busy ? '확인 중…' : '등록 마치기'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
